import React, { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

/**
 * Admin view of all Disha (Confidential Support) conversations.
 * Lists every student who has used Disha; click one to see the full chat
 * and optionally inject a manual reply (saved as sender:'disha', manual:true).
 *
 * Access controlled by Firestore rules (isAdmin()).
 */
export default function AdminDishaChats() {
  const { currentUser, userData } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [studentName, setStudentName] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState({}); // uid -> display name
  const [scanStatus, setScanStatus] = useState('');
  const [scanning, setScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyQueued, setOnlyQueued] = useState(false);

  const isAdmin = userData?.isAdmin || userData?.isSuperAdmin;

  // Set of uids that currently have at least one queued (unanswered) message.
  const queuedUids = React.useMemo(
    () => new Set(queue.map(q => q.userId).filter(Boolean)),
    [queue]
  );

  // Apply search + queued-only filter to the thread list.
  const visibleThreads = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return threads.filter(t => {
      if (onlyQueued && !queuedUids.has(t.uid)) return false;
      if (!term) return true;
      const nm = (names[t.uid] || '').toLowerCase();
      return nm.includes(term) || t.uid.toLowerCase().includes(term);
    });
  }, [threads, names, searchTerm, onlyQueued, queuedUids]);

  // Backfill / rebuild thread summary docs from existing message data.
  // Strategy: discover candidate uids from `dishaUsage` (plain collection, no
  // collectionGroup ambiguity), then for each uid read the latest message
  // from `dishaChats/{uid}/messages` and write the parent summary doc.
  const rebuildThreads = async () => {
    setScanning(true);
    setScanStatus('Scanning dishaUsage for known users…');
    try {
      const usageSnap = await getDocs(collection(db, 'dishaUsage'));
      const uids = new Set();
      usageSnap.docs.forEach(d => {
        // Doc ID format: "{uid}_{YYYY-MM-DD}"
        const id = d.id;
        const idx = id.lastIndexOf('_');
        if (idx > 0) uids.add(id.slice(0, idx));
        if (d.data().userId) uids.add(d.data().userId);
      });
      setScanStatus(`Found ${uids.size} user(s). Reading latest messages…`);
      let written = 0;
      for (const uid of uids) {
        try {
          const msgsSnap = await getDocs(query(
            collection(db, 'dishaChats', uid, 'messages'),
            orderBy('createdAt', 'desc'),
            limit(1)
          ));
          if (msgsSnap.empty) continue;
          const latest = msgsSnap.docs[0].data();
          await setDoc(doc(db, 'dishaChats', uid), {
            userId: uid,
            lastText: latest.text || '',
            lastSender: latest.sender || '',
            lastAt: latest.createdAt || serverTimestamp(),
          }, { merge: true });
          written++;
        } catch (e) {
          console.warn('rebuild failed for', uid, e);
        }
      }
      setScanStatus(`Done. Rebuilt ${written} thread summary doc(s).`);
    } catch (e) {
      console.error(e);
      setScanStatus('Scan failed: ' + e.message);
    } finally {
      setScanning(false);
    }
  };

  // Subscribe to dishaChats parent docs (each holds a summary: lastText, lastSender, lastAt).
  // This is the standard, consistent chat-thread index pattern and avoids collectionGroup ambiguity.
  useEffect(() => {
    if (!isAdmin) return;
    let didBackfill = false;
    const q = query(
      collection(db, 'dishaChats'),
      orderBy('lastAt', 'desc'),
      limit(200)
    );
    const unsub = onSnapshot(q, async snap => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          uid: d.id,
          lastText: data.lastText || '',
          lastSender: data.lastSender || '',
          lastAt: data.lastAt?.toMillis?.() || 0,
        };
      });
      setThreads(list);

      // Auto-backfill once if parent collection is empty. Keep `loading` true
      // during the backfill so the UI shows "Loading…" instead of flashing
      // "No chats yet" → "1 conversation".
      if (list.length === 0 && !didBackfill) {
        didBackfill = true;
        rebuildThreads().finally(() => setLoading(false));
      } else {
        setLoading(false);
      }

      // Fetch missing names for the visible threads
      setNames(prev => {
        const missing = list.map(t => t.uid).filter(uid => !(uid in prev));
        if (missing.length === 0) return prev;
        missing.forEach(async uid => {
          try {
            const u = await getDoc(doc(db, 'users', uid));
            const nm = u.exists() ? (u.data().username || u.data().email || uid) : uid;
            setNames(p => ({ ...p, [uid]: nm }));
          } catch {
            setNames(p => ({ ...p, [uid]: uid }));
          }
        });
        return prev;
      });
    }, err => {
      console.error(err);
      setLoading(false);
    });
    return unsub;
  }, [isAdmin]);

  // Load queued (unanswered) messages
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'dishaQueue'), orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(q, snap => {
      setQueue(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [isAdmin]);

  // Subscribe to selected thread
  useEffect(() => {
    if (!selectedUid) { setSelectedMessages([]); return; }
    const q = query(
      collection(db, 'dishaChats', selectedUid, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setSelectedMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    // Fetch student name
    (async () => {
      try {
        const u = await getDoc(doc(db, 'users', selectedUid));
        setStudentName(u.exists() ? (u.data().username || u.data().email || selectedUid) : selectedUid);
      } catch { setStudentName(selectedUid); }
    })();
    return unsub;
  }, [selectedUid]);

  const sendManualReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedUid) return;
    setSending(true);
    try {
      const text = reply.trim();
      await addDoc(collection(db, 'dishaChats', selectedUid, 'messages'), {
        sender: 'disha',
        text,
        provider: 'manual_admin',
        manual: true,
        adminUid: currentUser?.uid || null,
        createdAt: serverTimestamp(),
      });
      // Keep the parent summary in sync so the thread list shows the latest msg.
      await setDoc(doc(db, 'dishaChats', selectedUid), {
        lastText: text,
        lastSender: 'disha',
        lastAt: serverTimestamp(),
      }, { merge: true });
      // Manual reply resolves any queued (unanswered) msgs for this user.
      try {
        const qSnap = await getDocs(query(collection(db, 'dishaQueue'), where('userId', '==', selectedUid)));
        await Promise.all(qSnap.docs.map(d => deleteDoc(d.ref)));
      } catch (e) { console.warn('queue clear failed', e); }
      setReply('');
    } catch (err) {
      alert('Failed to send: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  if (!currentUser) return <div style={{ padding: 40 }}>Please sign in.</div>;
  if (!isAdmin) return <div style={{ padding: 40 }}>Admin access required.</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={{ margin: 0 }}>Disha — Confidential Support Admin</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            onClick={rebuildThreads}
            disabled={scanning}
            style={styles.scanBtn}
            title="Rescan dishaUsage and rebuild thread summary docs"
          >
            {scanning ? 'Scanning…' : '↻ Rescan threads'}
          </button>
          <Link to="/" style={styles.headerLink}>← Home</Link>
        </div>
      </div>
      {scanStatus && <div style={styles.scanStatus}>{scanStatus}</div>}

      {queue.length > 0 && (
        <div style={styles.queueBanner}>
          <strong>⚠ {queue.length} message(s) queued</strong> — all free AI providers
          were temporarily exhausted when these were sent. Reply manually or wait for quotas to reset.
        </div>
      )}

      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <div style={styles.sidebarTools}>
            <input
              type="text"
              placeholder="🔍 Search by name or uid…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button
              type="button"
              onClick={() => setOnlyQueued(v => !v)}
              style={{
                ...styles.filterBtn,
                ...(onlyQueued ? styles.filterBtnActive : {}),
              }}
              title="Show only users with queued (unanswered) messages"
            >
              {onlyQueued ? '✓ ' : ''}Queued only{queuedUids.size > 0 ? ` (${queuedUids.size})` : ''}
            </button>
          </div>
          <h4 style={{ margin: '8px 0 10px 0' }}>
            Conversations ({visibleThreads.length}{visibleThreads.length !== threads.length ? ` of ${threads.length}` : ''})
          </h4>
          {loading && <div>Loading…</div>}
          {!loading && visibleThreads.length === 0 && (
            <div style={{ color: '#888' }}>
              {threads.length === 0 ? 'No chats yet.' : 'No matches.'}
            </div>
          )}
          {visibleThreads.map(t => (
            <button
              key={t.uid}
              onClick={() => setSelectedUid(t.uid)}
              style={{
                ...styles.threadItem,
                background: selectedUid === t.uid ? '#f3eafb' : 'transparent',
                borderLeftColor: selectedUid === t.uid ? '#c06ee5' : 'transparent',
              }}
            >
              <div style={styles.threadName}>
                {names[t.uid] || 'Loading…'}
                {queuedUids.has(t.uid) && (
                  <span style={styles.queuedDot} title="Has queued msg(s)">●</span>
                )}
              </div>
              <div style={styles.threadPreview}>
                <em>{t.lastSender}:</em> {(t.lastText || '').slice(0, 60)}
              </div>
              <div style={styles.threadTime}>
                {t.lastAt ? new Date(t.lastAt).toLocaleString() : ''}
              </div>
            </button>
          ))}
        </aside>

        <main style={styles.main}>
          {!selectedUid ? (
            <div style={styles.empty}>Select a conversation to view.</div>
          ) : (
            <>
              <div style={styles.chatHead}>
                <strong>{studentName}</strong>
                <span style={styles.uidTag}>{selectedUid}</span>
              </div>
              <div style={styles.chatBox}>
                {selectedMessages.map(m => (
                  <div
                    key={m.id}
                    style={{
                      ...styles.msg,
                      ...(m.sender === 'user' ? styles.msgUser : styles.msgDisha),
                      ...(m.manual ? { outline: '2px solid #c06ee5' } : {}),
                    }}
                  >
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                    <div style={styles.msgMeta}>
                      {m.sender}
                      {m.provider ? ` · ${m.provider}` : ''}
                      {m.createdAt?.toDate ? ` · ${m.createdAt.toDate().toLocaleString()}` : ''}
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={sendManualReply} style={styles.replyBar}>
                <textarea
                  rows={2}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Add a manual reply (will appear as Disha)…"
                  style={styles.replyInput}
                  disabled={sending}
                />
                <button type="submit" disabled={sending || !reply.trim()} style={styles.replyBtn}>
                  {sending ? 'Sending…' : 'Send as Disha'}
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 20, fontFamily: 'system-ui, sans-serif', maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  headerLink: { color: '#c06ee5', textDecoration: 'none', fontWeight: 600 },
  scanBtn: { background: '#fff', border: '1px solid #c06ee5', color: '#c06ee5', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  scanStatus: { background: '#eef9ee', border: '1px solid #b7e2b7', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#2a572a' },
  queueBanner: { background: '#fff8e6', border: '1px solid #f3d57a', padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 14 },
  layout: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, minHeight: '70vh' },
  sidebar: { background: '#fafafe', border: '1px solid #eee', borderRadius: 12, padding: 12, overflowY: 'auto', maxHeight: '78vh' },
  threadItem: { display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderLeft: '3px solid transparent', cursor: 'pointer', marginBottom: 4, borderRadius: 6 },
  threadUid: { fontFamily: 'monospace', fontSize: 11, color: '#888' },
  threadName: { fontWeight: 600, fontSize: 14, color: '#222', display: 'flex', alignItems: 'center', gap: 6 },
  queuedDot: { color: '#e67e22', fontSize: 10, lineHeight: 1 },
  sidebarTools: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 4 },
  searchInput: { width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  filterBtn: { width: '100%', padding: '7px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#555', cursor: 'pointer', textAlign: 'left' },
  filterBtnActive: { background: '#fff4ea', borderColor: '#e67e22', color: '#b85a0a' },
  threadPreview: { fontSize: 13, color: '#222', margin: '3px 0' },
  threadTime: { fontSize: 11, color: '#aaa' },
  main: { background: '#fff', border: '1px solid #eee', borderRadius: 12, display: 'flex', flexDirection: 'column', minHeight: 0 },
  empty: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' },
  chatHead: { padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  uidTag: { fontFamily: 'monospace', fontSize: 11, color: '#888' },
  chatBox: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: '#fafafe', maxHeight: '60vh' },
  msg: { maxWidth: '78%', padding: '10px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.5 },
  msgUser: { alignSelf: 'flex-end', background: '#6a5cf5', color: '#fff' },
  msgDisha: { alignSelf: 'flex-start', background: '#fff', border: '1px solid #f0e6f5', color: '#222' },
  msgMeta: { marginTop: 6, fontSize: 10, opacity: 0.65 },
  replyBar: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #eee' },
  replyInput: { flex: 1, resize: 'none', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontFamily: 'inherit', fontSize: 14 },
  replyBtn: { background: 'linear-gradient(135deg, #ff6b9d, #c06ee5)', color: '#fff', border: 'none', padding: '0 18px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
};
